import clsx from 'clsx';
import styles from './Slide.module.scss';
import {
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type TransitionEvent,
} from 'react';

export type SlideDirection = 'bottom' | 'left' | 'right' | 'top';

interface ChildrenArgs {
  hide: (direction: SlideDirection) => void;
  show: (direction: SlideDirection) => void;
}

interface Props {
  children: ReactNode | ((args: ChildrenArgs) => ReactNode);
  direction?: SlideDirection;
  visible?: boolean;
  onHideEnd?: () => void;
  onShowEnd?: () => void;
}

// Safari does not reliably fire `transitionend` for every transition (e.g.
// when the before/after styles resolve within the same layout/paint cycle),
// which would otherwise leave onShowEnd/onHideEnd never called. This
// fallback timer guarantees they still fire, matched to the CSS duration
// (0.4s in Slide.module.scss) plus a small buffer so the real event wins
// when it does fire.
const TRANSITION_FALLBACK_MS = 450;

export function Slide({
  children,
  direction,
  onHideEnd,
  onShowEnd,
  visible: visibleProp,
}: Props) {
  const [transition, setTransition] = useState<string>();
  const [visible, setVisible] = useState<boolean>(false);
  const previousVisibleRef = useRef<boolean | undefined>(undefined);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(null);

  function hide(direction: SlideDirection) {
    setTransition('out-' + direction);
    setVisible(false);
  }

  function show(direction: SlideDirection) {
    setTransition('in-' + direction);
    setVisible(true);
  }

  function clearFallback() {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  }

  useLayoutEffect(() => {
    const nextVisible = visibleProp ?? true;
    const isFirstRun = previousVisibleRef.current === undefined;
    const didChange = !isFirstRun && previousVisibleRef.current !== nextVisible;
    previousVisibleRef.current = nextVisible;

    setVisible(nextVisible);
    if (direction) {
      nextVisible ? show(direction) : hide(direction);
    }

    clearFallback();

    // Only arm the fallback when the resolved visible value actually
    // changed. This is deliberately a value comparison rather than a
    // "have we mounted" flag: React 18 StrictMode replays this effect
    // (mount -> cleanup -> mount again) with identical props, and a simple
    // mounted-flag would treat that synthetic replay as a real transition
    // and fire onShowEnd/onHideEnd for nothing.
    if (!didChange) {
      return;
    }

    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = undefined;
      (nextVisible ? onShowEnd : onHideEnd)?.();
    }, TRANSITION_FALLBACK_MS);

    return clearFallback;
    // onShowEnd/onHideEnd are read at trigger time on purpose - including
    // them here would re-arm the fallback on every unrelated re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction, visibleProp]);

  function onTransitionEnd(event: TransitionEvent<HTMLDivElement>) {
    if (event.target !== event.currentTarget) {
      return;
    }
    clearFallback();
    (visible ? onShowEnd : onHideEnd)?.();
  }

  return (
    <div
      className={clsx(
        styles['slide'],
        transition && styles[transition],
        visible && styles['visible'],
      )}
      onTransitionEnd={onTransitionEnd}
    >
      {typeof children === 'function' ? children({ hide, show }) : children}
    </div>
  );
}
